import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config";
import MultiSelectDropdown from "../../../../../globalComponents/form/dropdowns/MultiSelectDropdown";
import QualitativeOwnerAssignedDrawer from "./QualitativeOwnerAssignedDrawer";

/**
 * Design tokens — mirrors BulkOwnerAssignModal so both data-management pages
 * share one visual language.
 */
const T = {
  blue: "#2563eb", blueL: "#eff6ff", blueB: "#bfdbfe",
  green: "#15803d", greenL: "#f0fdf4", greenB: "#bbf7d0",
  amber: "#b45309", amberL: "#fffbeb", amberB: "#fde68a",
  red: "#be123c", redL: "#fff1f2", redB: "#fecdd3",
  ink: "#0f172a", ink2: "#334155", ink3: "#64748b", ink4: "#94a3b8",
  rule: "#e2e8f0", rule2: "#f1f5f9", bg: "#f8fafc",
  white: "#ffffff",
};

const Spin = ({ size = 13, color = T.blue }) => (
  <span style={{ display: "inline-block", width: size, height: size, flexShrink: 0, border: `2px solid ${color}22`, borderTopColor: color, borderRadius: "50%", animation: "qoa-spin .6s linear infinite" }} />
);

const StepBadge = ({ n, done, active }) => (
  <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, background: done ? T.green : active ? T.blue : T.rule, color: T.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9.5, fontWeight: 800, transition: "background .2s" }}>
    {done ? "✓" : n}
  </div>
);

const StepRow = ({ n, label, done, active }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
    <StepBadge n={n} done={done} active={active} />
    <span style={{ fontSize: 12.5, fontWeight: 700, color: done || active ? T.ink : T.ink4 }}>{label}</span>
  </div>
);

/**
 * Bulk "Assign Data Owner" modal for the qualitative page — the qualitative
 * counterpart of BulkOwnerAssign/BulkOwnerAssignModal with the location
 * dimension removed: qualitative assignments are plain (question, user) pairs,
 * so only the user-first flow exists (no "Assign by Location" tab and no
 * per-owner location picker). Step 1 picks the data owners, step 2 shows a
 * review card per owner with drawers for their unassigned questions and
 * existing assignments (where assignments can be removed).
 *
 * Assigning always adds owners — the backend upserts (question, user) scope
 * rows, so questions that already have an owner keep it and re-assigning an
 * existing owner is a no-op.
 *
 * Props:
 *   show               {boolean}
 *   onHide             {function}
 *   selectedIds        {number[]}  — checked question IDs
 *   financialYearId    {number}
 *   selectedQuestions    {array}     — checked question rows, for titles in the drawers
 *   onSuccess            {function}  — called after successful save
 *   onAssignmentsChanged {function}  — called after a removal inside the drawer,
 *                                      so the page can background-refresh its
 *                                      Assigned To column while the modal stays open
 */
const QualitativeOwnerAssignModal = ({
  show,
  onHide,
  selectedIds = [],
  financialYearId,
  selectedQuestions = [],
  onSuccess,
  onAssignmentsChanged,
}) => {
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [allDataOwners, setAllDataOwners] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedOwnerIds, setSelectedOwnerIds] = useState([]);

  const [dbAssignments, setDbAssignments] = useState([]);
  const [loadingDbAssignments, setLoadingDbAssignments] = useState(false);

  const [drawer, setDrawer] = useState(null);

  const fetchDbAssignments = async () => {
    if (!selectedIds.length || !financialYearId) return;
    setLoadingDbAssignments(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}qualitative-data/assignments/${financialYearId}`,
        {},
        {},
        "GET"
      );
      if (isSuccess && isMounted.current) {
        setDbAssignments(data?.data?.assignments || []);
      }
    } catch (e) {
      console.error("Failed to fetch existing assignments", e);
    } finally {
      if (isMounted.current) setLoadingDbAssignments(false);
    }
  };

  /** Resets modal state and refetches owners + assignments on every open. */
  useEffect(() => {
    if (!show) return;
    setError("");
    setSelectedOwnerIds([]);
    setDbAssignments([]);
    setDrawer(null);
    fetchAllDataOwners();
    fetchDbAssignments();
  }, [show]);

  const fetchAllDataOwners = async () => {
    setLoadingUsers(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getSubUser`,
        {},
        {},
        "GET"
      );
      if (isSuccess && isMounted.current) {
        const filtered = (data?.data || []).filter((u) => {
          const roleLower = u.role?.toLowerCase() || "";
          return roleLower.includes("data owner") || roleLower.includes("data contributor");
        });
        const list = filtered.map((u) => ({
          id: String(u.userId),
          name: [u.firstName, u.lastName].filter(Boolean).join(" ") +
            (u.designation ? ` (${u.designation})` : ""),
        }));
        setAllDataOwners(list);
      }
    } catch {
      setError("Failed to fetch sub-users. Please try again.");
    } finally {
      if (isMounted.current) setLoadingUsers(false);
    }
  };

  const handleSave = async () => {
    setError("");
    if (!selectedOwnerIds.length) { setError("Select at least one data owner."); return; }
    setSaving(true);
    try {
      const userIds = selectedOwnerIds.map(Number);
      const { isSuccess } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}qualitative-data/assign`,
        {},
        {
          financialYearId,
          assignments: selectedIds.map((questionId) => ({
            questionId: Number(questionId),
            userIds,
          })),
        },
        "POST"
      );
      if (isSuccess) {
        onSuccess?.();
        onHide();
      } else {
        setError("Assignment failed. Please try again.");
      }
    } catch { setError("Server error. Please try again."); }
    finally { if (isMounted.current) setSaving(false); }
  };

  const questionTitles = useMemo(() => {
    const map = {};
    (selectedQuestions || []).forEach((q) => { map[q.questionId || q.id] = q.title; });
    return map;
  }, [selectedQuestions]);

  /**
   * Drawer removal hook: refreshes the modal's own assignment snapshot, then
   * lets the page silently refresh its Assigned To column too.
   */
  const handleAssignmentsRefresh = async () => {
    await fetchDbAssignments();
    onAssignmentsChanged?.();
  };

  const ownersFor = useCallback((qid) => {
    const item = dbAssignments.find((d) => Number(d.questionId) === Number(qid));
    return item?.users || [];
  }, [dbAssignments]);

  const ownerCards = useMemo(() => selectedOwnerIds.map((uid) => {
    const owner = allDataOwners.find((o) => o.id === uid);
    return {
      ownerId: uid,
      owner,
      unassignedQids: selectedIds.filter((qid) => !ownersFor(qid).some((u) => String(u.id) === String(uid))),
      assignedQids:   selectedIds.filter((qid) => ownersFor(qid).some((u) => String(u.id) === String(uid))),
    };
  }), [selectedOwnerIds, allDataOwners, selectedIds, ownersFor]);

  /**
   * Re-derives the open drawer's question ids from the current owner cards on
   * every render (not a click-time snapshot) — ownerCards recompute off
   * dbAssignments, so this keeps the drawer's list in sync after a removal
   * inside it triggers onRefreshAssignments (fetchDbAssignments).
   */
  const drawerCard = drawer ? ownerCards.find((c) => c.ownerId === drawer.scopeId) : null;
  const drawerQIds = drawer
    ? (drawer.mode === "unassigned" ? (drawerCard?.unassignedQids || []) : (drawerCard?.assignedQids || []))
    : [];

  if (!show) return null;

  const step1Done = selectedOwnerIds.length > 0;

  return (
    <>
      <style>{`@keyframes qoa-spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.48)", backdropFilter: "blur(4px)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <div style={{ background: T.white, borderRadius: 16, width: "100%", maxWidth: 600, height: "min(700px, 94vh)", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,.22)" }}>
          <div style={{ padding: "16px 20px 14px", borderBottom: `1px solid ${T.rule}`, display: "flex", alignItems: "center", gap: 11, flexShrink: 0 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: T.blueL, border: `1.5px solid ${T.blueB}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>👤</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.ink, letterSpacing: "-0.01em" }}>Assign Data Owners</div>
              <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}>
                <span>{selectedIds.length} Question{selectedIds.length !== 1 ? "s" : ""} selected</span>
                <span style={{ color: T.rule }}>·</span>
                <span style={{ color: T.blue, fontWeight: 600 }}>By User</span>
                {loadingDbAssignments && <><span style={{ color: T.rule }}>·</span><Spin size={10} /></>}
              </div>
            </div>
            <button onClick={onHide} disabled={saving} style={{ border: `1px solid ${T.rule}`, borderRadius: 8, background: T.bg, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 16, color: T.ink3 }}>×</button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

            {error && <div style={{ padding: "9px 13px", borderRadius: 8, background: T.redL, border: `1px solid ${T.redB}`, color: T.red, fontSize: 12.5, marginBottom: 14, display: "flex", gap: 8 }}><span>⚠️</span>{error}</div>}

            <div style={{ padding: "9px 13px", borderRadius: 8, background: T.blueL, border: `1px solid ${T.blueB}`, color: T.blue, fontSize: 12, marginBottom: 14, display: "flex", gap: 8 }}>
              <span>ℹ️</span>
              <span>
                Assigning a data owner applies to every selected question — including ones that already have an owner. This adds them as an additional owner; it never removes an existing one.
              </span>
            </div>
            <StepRow n={1} label="Select Data Owners" done={step1Done} active={!step1Done} />
            <div style={{ marginBottom: 18 }}>
              <MultiSelectDropdown
                options={allDataOwners.map((owner) => ({ value: owner.id, label: owner.name }))}
                selected={selectedOwnerIds}
                onChange={setSelectedOwnerIds}
                placeholder="— Choose Data Owners —"
                searchPlaceholder="Search data owners..."
                showSelectAll={true}
              />

              {loadingUsers && (
                <div style={{ marginTop: 8, padding: "7px 11px", borderRadius: 7, background: T.blueL, border: `1px solid ${T.blueB}`, fontSize: 11.5, color: T.blue, display: "flex", alignItems: "center", gap: 7 }}>
                  <Spin size={11} color={T.blue} /> Loading…
                </div>
              )}
            </div>
            {step1Done && (
              <>
                <StepRow n={2} label="Review Questions per Owner" done={ownerCards.length > 0} active={ownerCards.length > 0} />

                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {ownerCards.map((card) => {
                    if (!card.owner) return null;
                    return (
                      <div key={card.ownerId} style={{ border: `1.5px solid ${T.rule}`, borderRadius: 12, background: T.white }}>
                        <div style={{ padding: "10px 14px 9px", background: T.bg, borderBottom: `1px solid ${T.rule}`, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{card.owner.name}</span>
                            <button
                              onClick={() => setSelectedOwnerIds((prev) => prev.filter((id) => id !== card.ownerId))}
                              style={{ border: "none", background: "transparent", color: T.ink4, cursor: "pointer", fontSize: 15 }}
                            >
                              ×
                            </button>
                          </div>

                          <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button
                              onClick={() => setDrawer({ scopeId: card.ownerId, scopeLabel: card.owner.name, mode: "unassigned" })}
                              style={{ fontSize: 11, fontWeight: 700, color: T.ink2, background: T.white, border: `1px solid ${T.rule}`, borderRadius: 20, padding: "4px 11px", cursor: "pointer", whiteSpace: "nowrap" }}
                            >
                              Unassigned Questions ({card.unassignedQids.length}) →
                            </button>
                            {card.assignedQids.length > 0 && (
                              <button
                                onClick={() => setDrawer({ scopeId: card.ownerId, scopeLabel: card.owner.name, mode: "assignments" })}
                                style={{ fontSize: 11, fontWeight: 600, color: T.blue, background: T.blueL, border: `1px solid ${T.blueB}`, borderRadius: 20, padding: "4px 11px", cursor: "pointer", whiteSpace: "nowrap" }}
                              >
                                Assignments ({card.assignedQids.length}) →
                              </button>
                            )}
                          </div>
                        </div>

                        <div style={{ padding: "10px 14px", fontSize: 11, color: T.ink3 }}>
                          {card.assignedQids.length > 0 && (
                            <div style={{ marginBottom: card.unassignedQids.length ? 6 : 0 }}>
                              ℹ {card.assignedQids.length} of {selectedIds.length} question{card.assignedQids.length !== 1 ? "s" : ""} already {card.assignedQids.length !== 1 ? "have" : "has"} this owner — they keep it; re-assigning never removes anyone.
                            </div>
                          )}
                          {card.unassignedQids.length > 0 ? (
                            <div>
                              Saving adds <b>{card.owner.name}</b> as data owner to {card.unassignedQids.length === selectedIds.length ? "all" : "the remaining"} {card.unassignedQids.length} question{card.unassignedQids.length !== 1 ? "s" : ""}.
                            </div>
                          ) : (
                            <div>Already assigned to every selected question — nothing new to add for this owner.</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.rule}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", background: T.bg, borderRadius: "0 0 16px 16px" }}>
            <div style={{ fontSize: 11.5, color: T.ink3 }}>
              {ownerCards.length > 0 ? `${ownerCards.length} owner${ownerCards.length !== 1 ? "s" : ""} ready` : ""}
            </div>
            <div style={{ display: "flex", gap: 9 }}>
              <button onClick={onHide} disabled={saving} style={{ padding: "8px 18px", borderRadius: 8, border: `1.5px solid ${T.rule}`, background: T.white, color: T.ink2, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving || !selectedOwnerIds.length}
                style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: (saving || !selectedOwnerIds.length) ? "#93c5fd" : T.blue, color: T.white, fontSize: 13, fontWeight: 700, cursor: (saving || !selectedOwnerIds.length) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}
              >
                {saving ? <><Spin size={12} color={T.white} /> Saving…</> : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {drawer && (
        <QualitativeOwnerAssignedDrawer
          scopeId={drawer.scopeId}
          scopeLabel={drawer.scopeLabel}
          qIds={drawerQIds}
          mode={drawer.mode}
          questionTitles={questionTitles}
          financialYearId={financialYearId}
          onClose={() => setDrawer(null)}
          onRefreshAssignments={handleAssignmentsRefresh}
        />
      )}
    </>
  );
};

export default QualitativeOwnerAssignModal;
